import isPropValid from '@emotion/is-prop-valid';
import { ClippedHoverText } from '@components/ClippedHoverText';
import { brand } from '@utility/config';
import { Flag } from '@elements/Flag';
import { DateFormat, DateUtility } from '@utility/date';
import { Link } from 'react-router';
import { Package, Truck } from '@phosphor-icons/react';
import {
  type CSSProperties,
  forwardRef,
  type HTMLAttributes,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { cn } from '@skupreme/shared/ui';

export const differenceInDays = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  try {
    const Difference_In_Time = d2.getTime() - d1.getTime();
    return Difference_In_Time / (1000 * 3600 * 24);
  } catch (e) {
    return 0;
  }
};

const colors = [
  '#009688',
  '#095FA4',
  '#2196F3',
  '#388E3C',
  '#7E57C2',
  '#FFA000',
];

class currentIndex {
  static value = 0;
}

export const getDarkColor = () => {
  if (currentIndex.value === colors.length - 1) currentIndex.value = 0;
  return colors[(currentIndex.value += 1)];
};

/*
 * The timeline boxes below are extended with `styled(X)` across the timelines,
 * and those wrappers hand every prop down. Only real DOM attributes may reach
 * the `<div>`, as styled-components' own filter allowed before.
 */
function domOnly<T extends object>(props: T) {
  return Object.fromEntries(
    Object.entries(props).filter(([key]) => isPropValid(key)),
  );
}

type Geometry = {
  bgColor?: string;
  left?: number;
  width?: number;
  height?: number;
};

type TimelineBoxProps = HTMLAttributes<HTMLDivElement> & Geometry;

const px = (value?: number) =>
  value === undefined || value === null ? undefined : `${value}px`;

/*
 * Each box sets only the geometry it owns, and only when given, so a wrapper's
 * own CSS for the rest still applies.
 */
function box(displayName: string, owns: Omit<Geometry, 'bgColor'>) {
  const Box = forwardRef<HTMLDivElement, TimelineBoxProps>(
    ({ bgColor, left, width, height, style, ...rest }, ref) => (
      <div
        {...domOnly(rest)}
        ref={ref}
        style={{
          ...(bgColor !== undefined && { background: bgColor }),
          ...('left' in owns && left !== undefined && { left: px(left) }),
          ...('width' in owns && width !== undefined && { width: px(width) }),
          ...('height' in owns &&
            height !== undefined && { height: px(height) }),
          ...style,
        }}
      />
    ),
  );
  Box.displayName = displayName;
  return Box;
}

export const BackgroundColorHelper = box('BackgroundColorHelper', {});

export const AbsolutePositionHelper = box('AbsolutePositionHelper', {
  left: 0,
});

export const WidthHelper = box('WidthHelper', { left: 0, width: 0 });

export const HeightHelper = box('HeightHelper', {
  left: 0,
  width: 0,
  height: 0,
});

export const getProviderLabel = data => {
  const icons = {
    truck: <Truck className="sk:mr-2!" size={16} />,
    package: <Package className="sk:mr-2!" size={16} />,
    fba: (
      <img src="/images/marketplaces/fba.svg" alt="FBA" className="sk:size-4" />
    ),
  };

  const renderIcon = () => {
    if (icons[data.icon_type]) return icons[data.icon_type];
    return icons.package;
  };
  return (
    <div>
      <Link
        to={`/app/scm/storage-provider/${
          data.fulfillment_provider_id || data._id
        }`}
        className="sk:flex sk:items-center sk:text-fg sk:hover:text-fg"
      >
        {renderIcon()}
        <Flag width={16} name={data.marketplaceRegionName} />
        {data.is_FBA && icons.fba}
        {!data.is_FBA && (
          <span className="sk:text-xs sk:text-fg sk:underline">
            <ClippedHoverText
              name={data.fulfillment_provider_name || data.name}
            />
          </span>
        )}
      </Link>
    </div>
  );
};

const GRID_EDGE = 'sk:absolute sk:box-border sk:border-solid sk:border-edge';

const dateHeadingFormat = DateFormat.dateWithShortYear.replace(',', '');

export function DatesBar({
  DayWidth,
  marginLeft = 0,
  startDate,
  endDate,
  differenceInHours = 0,
  hourWidth = 0,
}) {
  const [weeks, setWeeks] = useState([]);
  const [days, setDays] = useState([]);
  const difference = useMemo(
    () => DateUtility.difference(startDate, endDate),
    [startDate, endDate],
  );

  const getDateOnly = date => new Date(date.toDateString());

  const toSkip = 7;

  useEffect(() => {
    if (!difference || difference < 1) return;

    if (!startDate || !endDate) return;
    const arr = [DateUtility.formatDate(startDate, dateHeadingFormat)];
    let endingDate = endDate;
    if (difference < 30) {
      endingDate = DateUtility.addDays(startDate, 30);
    }
    let currentDate = startDate;
    let end = true;
    while (end) {
      const nextDate = DateUtility.addDays(currentDate, toSkip);
      if (DateUtility.isDateAfter(nextDate, endingDate)) {
        end = false;
        break;
      }
      arr.push(DateUtility.formatDate(nextDate, dateHeadingFormat));
      currentDate = nextDate;
    }
    // @ts-expect-error - TS2345: Argument of type 'string[]' is not assignable to parameter of type 'SetStateActi...
    setWeeks(arr);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!difference || difference < 1) return;

    let date = startDate;
    const diff = weeks.length * 7;
    const arr = new Array(diff);
    for (let i = 0; i < diff; i += 1) {
      const weekDayName = DateUtility.formatDate(date, DateFormat.day);
      arr[i] = {
        name: weekDayName[0],
        today:
          DateUtility.difference(
            getDateOnly(new Date()),
            getDateOnly(new Date(date)),
          ) === 0,
      };
      date = DateUtility.addDays(date, 1);
    }

    // @ts-expect-error - TS2345: Argument of type 'any[]' is not assignable to parameter of type 'SetStateAction<...
    setDays(arr);
  }, [weeks, difference]);

  const spaceFromLeft = useMemo(
    () => differenceInDays(startDate, new Date()) * DayWidth,
    [startDate],
  );

  if (!difference) return <text />;

  return (
    <>
      <div className="sk:sticky sk:top-0 sk:z-10 sk:ml-52 sk:h-10 sk:select-none">
        {weeks.map((weekDate, index) => (
          <div
            className="sk:absolute sk:box-border sk:h-6.25 sk:bg-page sk:pl-1 sk:text-xs sk:text-fg sk:select-none"
            style={{
              width: DayWidth * 7,
              left:
                index * (DayWidth * 7) +
                marginLeft -
                differenceInHours * hourWidth,
            }}
          >
            {weekDate}
          </div>
        ))}
        {days.map((weekDate, index) => (
          <div
            className={cn(
              GRID_EDGE,
              index === 0
                ? 'sk:[border-width:0_1px_0_1px]'
                : 'sk:[border-width:0_1px_0_0]',
              'sk:top-6.25 sk:bg-page sk:text-center sk:text-xs sk:select-none',
              // @ts-expect-error - TS2339: Property 'today' does not exist on type 'never'.
              weekDate.today ? 'sk:text-fg' : 'sk:text-fg-tertiary',
            )}
            style={{
              width: DayWidth,
              left:
                index * DayWidth + marginLeft - differenceInHours * hourWidth,
            }}
          >
            {/* @ts-expect-error - TS2339: Property 'name' does not exist on type 'never'. */}
            {weekDate.name}
          </div>
        ))}
      </div>
      {days.map((_, index) => (
        <div
          className={cn(
            GRID_EDGE,
            index === 0
              ? 'sk:[border-width:0_1px_0_1px]'
              : 'sk:[border-width:0_1px_0_0]',
            'sk:h-[calc(100%-40px)] sk:bg-page sk:text-center sk:select-none',
          )}
          style={{
            width: DayWidth,
            left:
              index * DayWidth +
              208 +
              marginLeft -
              differenceInHours * hourWidth,
          }}
        />
      ))}
      <div
        className="sk:absolute sk:top-10.5 sk:z-1 sk:text-xs sk:font-bold sk:text-fg"
        style={{ left: spaceFromLeft + 218 }}
      >
        Today
      </div>
      <div
        className="sk:pointer-events-none sk:absolute sk:top-0 sk:left-0 sk:-z-1 sk:h-full sk:bg-page"
        style={{ width: spaceFromLeft + 208 }}
      />
    </>
  );
}

type NameContainerProps = HTMLAttributes<HTMLDivElement> & {
  textColor?: string;
  last?: boolean | number;
};

export const NameContainer = forwardRef<HTMLDivElement, NameContainerProps>(
  ({ textColor, last, className, style, ...rest }, ref) => (
    <div
      {...domOnly(rest)}
      ref={ref}
      className={cn(
        'sk:sticky sk:left-0 sk:z-121 sk:flex sk:min-w-52 sk:max-w-52 sk:cursor-pointer sk:items-center sk:gap-x-1.5 sk:bg-page sk:select-none',
        last ? 'sk:max-h-12 sk:min-h-12 sk:pb-5' : 'sk:max-h-7 sk:min-h-7',
        className,
      )}
      style={textColor ? { color: textColor, ...style } : style}
    />
  ),
);
NameContainer.displayName = 'NameContainer';

export const HeadingContainer = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div
    {...domOnly(rest)}
    ref={ref}
    className={cn(
      'sk:sticky sk:left-0 sk:h-0 sk:max-h-12.5 sk:min-h-12.5 sk:min-w-52 sk:max-w-52 sk:bg-page sk:select-none',
      'sk:[&_h3]:text-[15px] sk:[&_h3]:font-bold sk:[&_h3]:text-fg',
      className,
    )}
  />
));
HeadingContainer.displayName = 'HeadingContainer';

/* The label inside a section's `HeadingContainer`. */
export const HEADING_LABEL = 'sk:m-0 sk:text-sm sk:font-semibold sk:text-fg';

export const Circle = forwardRef<HTMLDivElement, TimelineBoxProps>(
  ({ className, ...rest }, ref) => (
    <BackgroundColorHelper
      {...rest}
      ref={ref}
      className={cn(
        'sk:size-2 sk:min-h-2 sk:min-w-2 sk:rounded-full',
        className,
      )}
    />
  ),
);
Circle.displayName = 'Circle';

export const BarName = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...rest }, ref) => (
  <div
    {...domOnly(rest)}
    ref={ref}
    className={cn(
      'sk:sticky sk:left-55 sk:flex sk:max-w-full sk:items-center sk:gap-x-1.25 sk:overflow-hidden sk:text-xs sk:text-ellipsis sk:whitespace-nowrap',
      'sk:[&_.icon]:min-w-3.75 sk:[&.dark-text]:text-fg',
      className,
    )}
  />
));
BarName.displayName = 'BarName';

type ShipmentBarProps = TimelineBoxProps & {
  HeightBar?: number;
  last?: boolean | number;
  opacity?: string | number;
  isClipped?: boolean;
};

/*
 * The fill goes through a custom property so the `buffer` outlines below can
 * clear it; an inline background would beat them.
 */
export const ShipmentBar = forwardRef<HTMLDivElement, ShipmentBarProps>(
  (
    {
      HeightBar,
      last,
      opacity,
      isClipped,
      bgColor,
      left,
      width,
      className,
      style,
      ...rest
    },
    ref,
  ) => (
    <div
      {...domOnly(rest)}
      ref={ref}
      className={cn(
        'sk:absolute sk:top-1/2 sk:z-11 sk:flex sk:rounded-[3px] sk:px-1.25 sk:text-white sk:select-none',
        'sk:bg-(--timeline-bar-bg) sk:transition-all sk:duration-300',
        /* `transform`, not Tailwind's `translate`: wrappers such as the stock-gap bar restate `transform`. */
        last
          ? 'sk:[transform:translateY(-20px)]'
          : 'sk:[transform:translateY(-50%)]',
        'sk:[&_a]:text-white sk:[&_a:hover]:text-white sk:[&_a:hover]:underline',
        'sk:[&_.fulltext-popup]:hidden sk:hover:[&_.fulltext-popup]:block',
        'sk:[&_.icon]:min-w-3.75',
        'sk:[&.buffer]:rounded-none sk:[&.buffer]:bg-transparent sk:[&.buffer]:text-fg',
        'sk:[&.buffer]:border-2 sk:[&.buffer]:border-dashed sk:[&.buffer]:border-fg-tertiary',
        'sk:[&.buffer-qty]:rounded-none sk:[&.buffer-qty]:bg-transparent sk:[&.buffer-qty]:text-fg',
        'sk:[&.buffer-qty]:border-2 sk:[&.buffer-qty]:border-dashed sk:[&.buffer-qty]:border-fg',
        'sk:[&.dim]:border-2 sk:[&.dim]:border-dashed sk:[&.dim]:border-fg',
        'sk:[&.add-sales-velocity]:cursor-pointer sk:[&.add-sales-velocity]:bg-transparent sk:[&.add-sales-velocity]:text-caution sk:[&.add-sales-velocity]:underline',
        'sk:[&.add-sales-velocity]:border-2 sk:[&.add-sales-velocity]:border-dashed sk:[&.add-sales-velocity]:border-caution sk:[&.add-sales-velocity:hover]:no-underline',
        isClipped && 'sk:rounded-r-none',
        className,
      )}
      style={
        {
          '--timeline-bar-bg': bgColor,
          left: px(left),
          width: px(width),
          height: px(HeightBar),
          opacity,
          ...style,
        } as CSSProperties
      }
    />
  ),
);
ShipmentBar.displayName = 'ShipmentBar';

type DrawerActionButtonsProps = HTMLAttributes<HTMLDivElement> & {
  type?: 'success' | 'warning' | 'default' | string;
};

/* The coloured bar along the bottom of a timeline drawer. */
export function DrawerActionButtons({
  type,
  className,
  ...rest
}: DrawerActionButtonsProps) {
  return (
    <div
      {...rest}
      className={cn(
        'actions',
        'sk:sticky sk:bottom-0 sk:z-1000 sk:mt-3.5 sk:flex sk:w-full sk:items-center sk:justify-between sk:p-4 sk:pr-25 sk:text-surface',
        type === 'success'
          ? 'sk:bg-success'
          : type === 'warning'
            ? 'sk:bg-warning'
            : 'sk:bg-fg-tertiary',
        'sk:[&_div.warning-info]:flex sk:[&_div.warning-info]:items-center sk:[&_div.warning-info]:text-[15px] sk:[&_div.warning-info]:font-bold',
        'sk:[&_div.info]:flex sk:[&_div.info]:items-center sk:[&_div.info]:text-xl',
        'sk:[&_div.actions]:flex sk:[&_div.actions]:flex-nowrap sk:[&_div.actions]:items-center sk:[&_div.actions]:justify-end',
        'sk:[&_div.actions>.blink]:h-9 sk:[&_div.actions>.blink_input]:max-w-17.5',
        className,
      )}
    />
  );
}

export const timelineToolTipsText = {
  suggestedShipment: `This is the predicted shipment quantity as suggested by ${brand.name}. This is the carton rounded value, see the detailed equation below for the raw value`,
  minDesiredStock:
    'This is the minimum inventory level (in days) configured for this item. You can modify this both on the storage provider settings page or in the Calculation Variables section below',
  expected:
    'These are all inbounding units in shipments that are still in progress',
  present:
    'These are the available inventory at the destination storage provider',
  reserved: 'These are the reserved FC transfer inventory only',
  originFP: 'This is the storage provider you selected as the origin',
  destinationFP: 'This is the storage provider you selected as the destination',
  calculationVariable: `These are the parameters affecting the prediction equation. You cannot modify system defaults please contact ${brand.name} if you wish update default values`,
  minDesiredStockCalculated:
    'This is the minimum inventory value (in days). If you do not apply a manual value, we may take it from the storage provider settings value',
  shippingBuffer:
    'This is the inbound buffer of your destination storage provider. The default value can be configured on your storage provider settings. Shipping Buffer and Transit Time are both components of the lead time',
  transitTime:
    'This is the time in days for this shipment. Transit Time and Shipping Buffer are both components of the lead time',
  lightningDeal:
    'If you know there will be a surge in sales in the near future due to seasonality or an advertising/promotional surge, add this here. We will adjust the values of the sales velocity in order to anticipate for a higher pace of sales in the designated date ranges',
  cartonRounding:
    'We round suggested quantities to the nearest whole number that is evenly divisible by the units per carton property at the warehouse. If there are insufficient units at the warehouse, we recommend the maximum amount available and suggest a shipment replenishment.',
  leadTime:
    'This is the total transit buffer which is a combination of the transit estimate, the preparation buffer, and the inbound processing buffers at the origin and destination storage providers which this shipment prediction has been prepared for.',
  minBuffer:
    'This is the minimum desired inventory configured for the primary warehouse net any urgent shipments to its destination providers',
  existingInventory:
    'This is the total available stock, reserved stock that is in FC/warehouse transfer, as well as any expected stock arriving at the destination storage provider.',
  MOQ: 'If the value is less than the MOQ, we automatically suggest the MOQ to comply with supplier requirements',
  minShipmentThreshold:
    'The Shipment Threshold is a buffer which can help control the frequency of shipments. The value is set in days. If your Current Stock and Expected Stock exceeds the Minimum Desired Stock and Lead Time but less than this threshold, we will not suggest the shipment.',
  negativeRawUnits:
    'A negative suggested value implies there is sufficient inventory or inbound shipments to meet buffer requirements.',
  suggestionLowerThanRaw:
    'Warehouse inventory is insufficient to satisfy suggested quantity. Please review purchase order replenishment suggestions in order to restock at your warehouse.',
  salesVelocity: (
    <span>
      {brand.name} excludes the Out Of Stock days when considering sales
      velocity.
      <br />
      <br />
      For example: If on a 30-day window of time, there are only 20 days
      in-stock. The 30-days window sales velocity would be the 900 units / 20
      days = 45 units / day.
    </span>
  ),
};
